import { PaidUsageLimit } from "../usage/PaidUsageLimit";
import { useActiveOrgId } from "../../state/orgs";
import { CameraIcon } from "lucide-react";
import { useRef, useState } from "react";

import { ChangeEmailDialog } from "./ChangeEmailDialog";
import { ChangeNameDialog } from "./ChangeNameDialog";

import {
  accountInitials,
  displayName,
  displaySubject,
  updateProfilePicture,
  useAccountProfile,
  type WorkspaceAccount,
} from "../../state/accountProfile";
import { Button } from "../ui/button";
import { encodeAvatarFile } from "./AccountSettings.logic";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";

function AccountAvatarPreview({ account }: { account: WorkspaceAccount }) {
  return account.profilePicture ? (
    <img
      alt="Current profile picture"
      className="size-16 rounded-full object-cover"
      referrerPolicy="no-referrer"
      src={account.profilePicture}
    />
  ) : (
    <span className="flex size-16 items-center justify-center rounded-full bg-muted font-semibold text-lg text-foreground">
      {accountInitials(account)}
    </span>
  );
}

function ProfilePictureControl({ account }: { account: WorkspaceAccount }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyPicture = async (profilePicture: string | null) => {
    setIsSaving(true);
    setError(null);
    const failure = await updateProfilePicture(profilePicture);
    setError(failure);
    setIsSaving(false);
  };

  const handleFilePicked = async (file: File | undefined) => {
    if (!file) return;
    // Saving covers the decode too, so a second pick (or a Remove) cannot
    // start while this one is still encoding and land out of order.
    setIsSaving(true);
    setError(null);
    try {
      const encoded = await encodeAvatarFile(file);
      const failure = await updateProfilePicture(encoded);
      setError(failure);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That image could not be read.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-2 sm:items-end">
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          className="sr-only"
          type="file"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            // Allow re-picking the same file after an error.
            event.currentTarget.value = "";
            void handleFilePicked(file);
          }}
        />
        <button
          type="button"
          aria-label="Change profile picture"
          disabled={isSaving}
          className="group relative size-16 shrink-0 overflow-hidden rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-wait"
          onClick={() => fileInputRef.current?.click()}
        >
          <AccountAvatarPreview account={account} />
          <span
            aria-hidden="true"
            className={`absolute inset-0 flex items-center justify-center rounded-full bg-black/45 transition-opacity ${
              isSaving
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
            }`}
          >
            <CameraIcon className="size-5 text-white" />
          </span>
        </button>
        {account.profilePicture ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={isSaving}
            onClick={() => void applyPicture(null)}
          >
            Remove
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function NameControl({ account }: { account: WorkspaceAccount }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // A fresh key per opening re-seeds the dialog with the current name.
  const [openCount, setOpenCount] = useState(0);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm text-muted-foreground">{displayName(account) ?? "—"}</span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setOpenCount((count) => count + 1);
          setDialogOpen(true);
        }}
      >
        Change
      </Button>
      <ChangeNameDialog
        key={openCount}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentFirstName={account.firstName ?? ""}
        currentLastName={account.lastName ?? ""}
      />
    </div>
  );
}

function EmailControl({ email }: { email: string }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm text-muted-foreground">{email}</span>
      <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
        Change
      </Button>
      <ChangeEmailDialog open={dialogOpen} onOpenChange={setDialogOpen} currentEmail={email} />
    </div>
  );
}

/**
 * `/settings/account` — the signed-in identity: profile picture and name
 * (both editable, stored on the platform user so app.amoreg.com agrees), and
 * the sign-in email with its OTP-verified change flow.
 */
export function AccountSettingsPanel() {
  const organizationId = useActiveOrgId();
  const { account, isPending } = useAccountProfile();

  if (!account) {
    return (
      <SettingsPageContainer>
        <SettingsSection id="account" title="Account">
          <p className="px-3 py-3 text-sm text-muted-foreground sm:px-4">
            {isPending
              ? "Loading your account…"
              : "No workspace account on this session. Sign in through Amoreg to manage your profile."}
          </p>
        </SettingsSection>
      </SettingsPageContainer>
    );
  }

  // Only WorkOS sessions have a platform user row to write the picture and name to.
  const canEditProfile = account.subject.startsWith("workos:");

  return (
    <SettingsPageContainer>
      <SettingsSection id="account" title="Account">
        <SettingsRow
          {...searchableSetting("profile-picture")}
          description={
            canEditProfile
              ? "Click the picture to upload a new one. Shown in the account menu and next to your activity; cropped to a square."
              : "This session signed in without an Amoreg platform profile, so its picture cannot be changed here."
          }
          control={
            canEditProfile ? <ProfilePictureControl account={account} /> : (
              <AccountAvatarPreview account={account} />
            )
          }
        />
        <SettingsRow
          {...searchableSetting("account-name")}
          description={
            canEditProfile
              ? "How you appear to your team: in the account menu, the team roster and next to your activity."
              : "Your name from the Amoreg platform profile."
          }
          control={
            canEditProfile ? (
              <NameControl account={account} />
            ) : (
              <span className="text-sm text-muted-foreground">{displayName(account) ?? "—"}</span>
            )
          }
        />
        <SettingsRow
          {...searchableSetting("account-email")}
          description={
            canEditProfile
              ? "The address you sign in with. Changing it emails a verification code to the new address first."
              : "The address this workspace session is signed in as."
          }
          control={
            canEditProfile ? (
              <EmailControl email={account.email ?? displaySubject(account.subject)} />
            ) : (
              <span className="truncate text-sm text-muted-foreground">
                {account.email ?? displaySubject(account.subject)}
              </span>
            )
          }
        />
      </SettingsSection>
      <SettingsSection id="account-usage" title="Usage">
        <div className="px-4 py-4"><PaidUsageLimit organizationId={organizationId} /></div>
      </SettingsSection>
    </SettingsPageContainer>
  );
}
